import { useEffect, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { z } from 'zod';
import { meOptions } from '@/features/auth/api';
import { projectsListOptions } from '@/features/projects/api';
import { timelineOptions } from '@/features/timeline/api';
import { taskStatusOptions, useTaskStatuses } from '@/features/tasks/api';
import { SummaryGantt } from '@/components/shared/SummaryGantt';
import {
  CalloutPanel,
  DeploymentsStrip,
  EpicProgressList,
  NotableTasks,
  ProgressLine,
  SummaryHeader,
  buildSummaryProgress,
  bucketNotableTasks,
  deploymentInPeriod,
  summaryWorkloadOptions,
  type Period,
} from '@/features/summary';
import { buildUtilizationIndex } from '@/lib/overload';
import { isTaskDone, isTaskInFlight } from '@/lib/status-kind';

const dateRe = /^\d{4}-\d{2}-\d{2}$/;

const summarySearchSchema = z
  .object({
    from: z.string().regex(dateRe).optional(),
    to: z.string().regex(dateRe).optional(),
    callout: z.string().max(2000).optional(),
  })
  .refine((s) => !(s.from && s.to) || s.from <= s.to, {
    path: ['to'],
    message: 'to must be on or after from',
  });

export type SummarySearch = z.infer<typeof summarySearchSchema>;

export const Route = createFileRoute('/_authed/projects/$projectId/timeline_/summary')({
  validateSearch: summarySearchSchema,
  loaderDeps: ({ search }) => ({ from: search.from, to: search.to }),
  loader: async ({ context, params, deps }) => {
    if (!deps.from || !deps.to) return;
    await Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectsListOptions()),
      context.queryClient.ensureQueryData(timelineOptions(params.projectId)),
      context.queryClient.ensureQueryData(taskStatusOptions(params.projectId)),
      context.queryClient.ensureQueryData(
        summaryWorkloadOptions({
          project_id: params.projectId,
          from: deps.from,
          to: deps.to,
        }),
      ),
    ]);
  },
  component: SummaryPage,
});

function todayIso(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

function fallbackPeriod(): Period {
  const today = todayIso();
  return { from: today, to: today };
}

function SummaryPage() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();

  const period: Period = useMemo(() => {
    if (search.from && search.to) return { from: search.from, to: search.to };
    return fallbackPeriod();
  }, [search.from, search.to]);

  const { data: me } = useSuspenseQuery(meOptions());
  const { data: projects } = useSuspenseQuery(projectsListOptions());
  const { data: timeline } = useSuspenseQuery(timelineOptions(projectId));
  const { data: workload } = useSuspenseQuery(
    summaryWorkloadOptions({ project_id: projectId, from: period.from, to: period.to }),
  );
  const { statusesById } = useTaskStatuses(projectId);

  const project = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);

  const progress = useMemo(() => {
    const isDone = (statusId: string) => isTaskDone(statusId, statusesById);
    return buildSummaryProgress(timeline, period, isDone);
  }, [timeline, period, statusesById]);

  const utilization = useMemo(() => buildUtilizationIndex(workload), [workload]);
  const today = useMemo(() => todayIso(), []);

  const notable = useMemo(() => {
    const isDone = (statusId: string) => isTaskDone(statusId, statusesById);
    const isInFlight = (statusId: string) => isTaskInFlight(statusId, statusesById);
    return bucketNotableTasks(progress.inPeriodTasks, utilization, today, isDone, isInFlight);
  }, [progress.inPeriodTasks, utilization, today, statusesById]);

  const deployments = useMemo(
    () => timeline.deployments.filter((d) => deploymentInPeriod(d, period)),
    [timeline.deployments, period],
  );

  useEffect(() => {
    document.body.dataset.printRoot = 'summary';
    return () => {
      delete document.body.dataset.printRoot;
    };
  }, []);

  useEffect(() => {
    const BLANK_ICON =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    const projectName = project?.name ?? 'Project';
    const restorers: Array<() => void> = [];

    const handleBeforePrint = () => {
      const originalTitle = document.title;
      document.title = `${projectName} - Summary (${period.from} to ${period.to})`;
      restorers.push(() => {
        document.title = originalTitle;
      });

      const iconLink = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
      if (iconLink) {
        const originalHref = iconLink.href;
        const originalType = iconLink.type;
        iconLink.type = 'image/png';
        iconLink.href = BLANK_ICON;
        restorers.push(() => {
          iconLink.href = originalHref;
          iconLink.type = originalType;
        });
      }

      const originalUrl = window.location.href;
      window.history.replaceState(null, '', '/');
      restorers.push(() => window.history.replaceState(null, '', originalUrl));
    };

    const handleAfterPrint = () => {
      while (restorers.length) restorers.pop()!();
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);
    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
      handleAfterPrint();
    };
  }, [project?.name, period.from, period.to]);

  return (
    <div className="summary-print-root mx-auto max-w-[1180px] px-6 py-6">
      <SummaryHeader
        projectId={projectId}
        projectName={project?.name ?? 'Project'}
        period={period}
        generatedBy={me.display_name}
        generatedAt={new Date()}
      />

      <ProgressLine progress={progress} />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[3fr_2fr]">
        <EpicProgressList perEpic={progress.perEpic} />
        <NotableTasks buckets={notable} statusesById={statusesById} />
      </div>

      <div className="mt-6 space-y-4 break-inside-avoid">
        <DeploymentsStrip deployments={deployments} />

        {search.callout ? <CalloutPanel text={search.callout} author={me.display_name} /> : null}
      </div>

      {/* Timeline last so it prints on its own page (`break-before-page`); the
          executive summary — completion, epic progress, deployments — fills
          page one. */}
      <div className="mt-6 break-before-page">
        <SummaryGantt data={timeline} window={period} zoom="week" statusesById={statusesById} />
      </div>

      <footer className="mt-8 flex items-center justify-between border-t border-border pt-3 text-xs text-text-faint">
        <span>
          Generated from PM Dashboard
          <span className="mx-2 text-text-faint" aria-hidden="true">
            ·
          </span>
          <span className="tabular-nums">{project?.name ?? projectId}</span>
        </span>
        <span className="tabular-nums">
          Window: {period.from} → {period.to}
          <span className="mx-2 text-text-faint" aria-hidden="true">
            ·
          </span>
          {format(new Date(), 'yyyy-MM-dd HH:mm')}
        </span>
      </footer>
    </div>
  );
}
